import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { collection, query, onSnapshot, getDocs, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase'; // Pastikan path ini benar
import { migrateUserChildrenToCentral } from '../helpers/migration';

// Konteks dibuat dengan struktur data yang diharapkan oleh HomeScreen,
// yaitu `myChildren` dan `allChildren`.
const ChildContext = createContext({
    myChildren: [],
    allChildren: [],
    sharedChildren: [],
    loading: true,
    error: null,
});

export const useChildren = () => {
    const context = useContext(ChildContext);
    if (context === undefined) {
        throw new Error('useChildren must be used within a ChildProvider. Pastikan komponen Anda dibungkus olehnya.');
    }
    return context;
};

export const ChildProvider = ({ user, children }) => {
    const [myChildrenList, setMyChildrenList] = useState([]);
    const [allChildrenList, setAllChildrenList] = useState([]);
    const [sharedChildrenList, setSharedChildrenList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // useCallback digunakan untuk memoize fungsi fetch data.
    const fetchData = useCallback(() => {
        if (!user || !user.uid) {
            setLoading(false);
            setMyChildrenList([]);
            setAllChildrenList([]);
            return () => {}; // Fungsi cleanup kosong
        }

        setLoading(true);
        setLoading(true);
        setError(null); // Reset error setiap kali fetch baru

        // Jalankan migrasi otomatis
        migrateUserChildrenToCentral(user).catch(console.error);

        // --- PERBAIKAN DI SINI ---
        // Fetch dari root `children` collection berdasarkan roles (Centralized Architecture)
        const myChildrenQuery = query(collection(db, 'children'), where('parents', 'array-contains', user.uid));
        
        const unsubscribeMyChildren = onSnapshot(myChildrenQuery, async (snapshot) => {
            const childrenData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Ambil pemeriksaan terakhir untuk setiap anak
            const childrenWithLastAssessment = await Promise.all(childrenData.map(async (child) => {
                const assessmentsQuery = query(
                    collection(db, `children/${child.id}/assessments`),
                    orderBy('createdAt', 'desc'),
                    limit(1)
                );
                const assessmentSnapshot = await getDocs(assessmentsQuery);
                const lastAssessment = assessmentSnapshot.docs.length > 0 
                    ? { id: assessmentSnapshot.docs[0].id, ...assessmentSnapshot.docs[0].data() }
                    : null;
                return { ...child, lastAssessment };
            }));

            childrenWithLastAssessment.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
            setMyChildrenList(childrenWithLastAssessment);
            
            if (user.role !== 'Tenaga Kesehatan' && user.role !== 'Guru PAUD') {
                setLoading(false);
            }
        }, (err) => {
            console.error("Error fetching 'myChildren':", err);
            setError("Gagal memuat data anak Anda.");
            setLoading(false);
        });

        // Fetch shared children for healthcare workers/teachers
        let unsubscribeSharedChildren = () => {};
        if (user.role === 'Tenaga Kesehatan' || user.role === 'Guru PAUD') {
            console.log('Healthcare worker/Guru PAUD detected, fetching shared children for user:', user.uid);
            
            const roleField = user.role === 'Tenaga Kesehatan' ? 'nakes' : 'teachers';
            const sharedAccessQuery = query(collection(db, 'children'), where(roleField, 'array-contains', user.uid));
            
            unsubscribeSharedChildren = onSnapshot(sharedAccessQuery, { includeMetadataChanges: true }, async (snapshot) => {
                if (snapshot.metadata.hasPendingWrites) {
                    console.log('Ignoring local snapshot with pending writes to prevent permission denied errors.');
                    return;
                }
                
                console.log('Shared access query result:', snapshot.size, 'documents found');
                const sharedAccessData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log('Shared access data:', sharedAccessData);
                
                if (sharedAccessData.length === 0) {
                    console.log('No shared access found for healthcare worker/Guru PAUD');
                    setSharedChildrenList([]);
                    setLoading(false);
                    return;
                }
                
                const sharedChildrenData = await Promise.all(sharedAccessData.map(async (childData) => {
                    try {
                        console.log(`Fetching child data for childId: ${childData.id}`);
                        
                        // Get last assessment
                        const assessmentsQuery = query(
                            collection(db, `children/${childData.id}/assessments`),
                            orderBy('createdAt', 'desc'),
                            limit(1)
                        );
                        const assessmentSnapshot = await getDocs(assessmentsQuery);
                        const lastAssessment = assessmentSnapshot.docs.length > 0 
                            ? { id: assessmentSnapshot.docs[0].id, ...assessmentSnapshot.docs[0].data() }
                            : null;
                        
                        console.log('Last assessment for child:', lastAssessment);
                        
                        return { ...childData, lastAssessment, isShared: true };
                    } catch (err) {
                        console.error(`Error fetching shared child ${childData.id}:`, err);
                        return null;
                    }
                }));

                const validSharedChildren = sharedChildrenData.filter(child => child !== null);
                console.log('Valid shared children:', validSharedChildren);
                setSharedChildrenList(validSharedChildren);
                setLoading(false);
            }, (err) => {
                console.error("Error fetching shared children:", err);
                setError("Gagal memuat data anak yang dibagikan.");
                setLoading(false);
            });
        } else {
            // For non-healthcare workers, ensure shared children is empty and loading is false
            setSharedChildrenList([]);
            if (user.role !== 'Tenaga Kesehatan' && user.role !== 'Guru PAUD') {
                setLoading(false);
            }
        }

        // Bagian ini untuk "HealthWorkerDashboard" - all children (if needed)
        let unsubscribeAllChildren = () => {};
        if (user.role === 'Tenaga Kesehatan') {
            // For now, we'll use shared children as the main data source
            // This can be expanded later if needed for a global view
            setAllChildrenList([]); // Clear all children for now
            if (!unsubscribeSharedChildren) {
                setLoading(false);
            }
        }
        
        // Mengembalikan fungsi cleanup yang akan menghentikan semua listener.
        return () => {
            unsubscribeMyChildren();
            unsubscribeAllChildren();
            unsubscribeSharedChildren();
        };

    }, [user]); // Dependensi 'user' memastikan fungsi ini berjalan saat user berubah.

    useEffect(() => {
        const unsubscribe = fetchData();
        return unsubscribe; // Cleanup akan memanggil fungsi unsubscribe saat komponen di-unmount.
    }, [fetchData]);

    // Memoize nilai context untuk mencegah re-render yang tidak perlu.
    const value = useMemo(() => ({
        myChildren: myChildrenList,
        allChildren: allChildrenList,
        sharedChildren: sharedChildrenList,
        loading,
        error,
    }), [myChildrenList, allChildrenList, sharedChildrenList, loading, error]);

    return (
        <ChildContext.Provider value={value}>
            {children}
        </ChildContext.Provider>
    );
};
